import type { PostInputMetaData } from '../../graphqlTypes'
import { canDiscloseAiContent } from '~publish/legacy/composer/composer/components/channel-fields/components/aiDisclosure'
import type { Draft } from '~publish/legacy/composer/composer/entities/Draft'
import { AttachmentTypes } from '~publish/legacy/constants'

import type { ChannelVariant } from '../../../store/postSlice/types'
import type { MapperContext } from '../../types'
import { clearable } from '../shared/clearable'
import type { ServiceMapperEntry } from '../types'

// A photo post has no AI-disclosure field in the composer, but the draft
// keeps the stored flag from an earlier video post; the backend rejects it,
// so this mirrors `canDiscloseAiContent`'s TikTok branch (`!isReminder &&
// !hasImagesAttachment`) off the variant's own fields instead of a Draft
// method call.
function canVariantDiscloseAiContent(variant: ChannelVariant): boolean {
  const hasImagesAttachment =
    variant.enabledAttachmentType === AttachmentTypes.MEDIA &&
    variant.assets.some((asset) => asset.kind === 'image')
  return !variant.isReminder && !hasImagesAttachment
}

// `title` (photo posts) and `isAiGenerated` (video posts) from
// `channelData.tiktok`, tri-state via `clearable`. On a reminder save, the
// legacy field config sends neither (TikTok has no reminder fields), so both
// are treated as absent regardless of stored channelData.
//
// `ctx.channelVariant` (when present) supplies both the reminder flag and
// `channelData`.
export function mapTiktok(draft: Draft, ctx: MapperContext): PostInputMetaData {
  const variant = ctx.channelVariant
  const isReminder = variant ? variant.isReminder : draft.isReminder
  const tiktok = isReminder
    ? undefined
    : (variant ? variant.channelData : draft.channelData)?.tiktok
  const canDisclose = variant
    ? canVariantDiscloseAiContent(variant)
    : canDiscloseAiContent(draft)

  return {
    tiktok: {
      title: clearable(tiktok?.title, ctx),
      isAiGenerated: clearable(
        canDisclose ? tiktok?.is_ai_generated : undefined,
        ctx,
      ),
    },
  }
}

export const tiktok: ServiceMapperEntry = {
  mapper: mapTiktok,
}
